import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

/** ------------------ Simple Toast (no external library) ------------------ */
function ToastStack({ toasts, onRemove }) {
  const stackRef = useRef(null);

  useEffect(() => {
    if (!stackRef.current) return;
    gsap.fromTo(
      stackRef.current.querySelectorAll(".toast-item"),
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.2, stagger: 0.03, ease: "power2.out" }
    );
  }, [toasts]);

  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[9999] w-[min(92vw,380px)]">
      <div ref={stackRef} className="flex flex-col gap-2">
        {toasts.map((t) => {
          const styles =
            t.type === "success"
              ? "border-green-200 bg-green-50 text-green-900"
              : t.type === "error"
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-amber-200 bg-amber-50 text-amber-900";

          return (
            <div
              key={t.id}
              className={[
                "toast-item pointer-events-auto rounded-xl border px-4 py-3 shadow-sm",
                styles,
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm">
                  {t.title && <div className="font-semibold">{t.title}</div>}
                  <div className="mt-0.5">{t.message}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(t.id)}
                  className="rounded-lg border bg-white/60 px-2 py-1 text-xs font-semibold hover:bg-white"
                  aria-label="Dismiss"
                >
                  x
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function clampNumber(value, min, max) {
  const rawValue = String(value ?? "").trim();
  if (rawValue === "") return "";

  const n = Number(rawValue);
  if (!Number.isFinite(n)) return "";
  return String(Math.min(max, Math.max(min, n)));
}

function hasValue(value) {
  return String(value ?? "").trim() !== "";
}

function roundTax(value) {
  return Math.round(value * 100) / 100;
}

function isValidTaxRate(value) {
  return Number.isFinite(value) && value >= 0 && value <= 100;
}

function deriveTaxPayload({ igst, cgst, sgst }) {
  const hasIgst = hasValue(igst);
  const hasCgst = hasValue(cgst);
  const hasSgst = hasValue(sgst);

  if (!hasIgst && !hasCgst && !hasSgst) {
    return { error: "Enter either IGST or both CGST and SGST." };
  }

  if (hasIgst && !hasCgst && !hasSgst) {
    const igstValue = Number(igst);
    if (!isValidTaxRate(igstValue)) {
      return { error: "IGST must be between 0 and 100." };
    }

    const halfTax = roundTax(igstValue / 2);
    return {
      mode: "igst",
      igst: String(roundTax(igstValue)),
      cgst: String(halfTax),
      sgst: String(halfTax),
    };
  }

  if (!hasIgst && hasCgst && hasSgst) {
    const cgstValue = Number(cgst);
    const sgstValue = Number(sgst);

    if (![cgstValue, sgstValue].every(isValidTaxRate)) {
      return { error: "CGST and SGST must be between 0 and 100." };
    }

    const igstValue = roundTax(cgstValue + sgstValue);
    if (!isValidTaxRate(igstValue)) {
      return { error: "CGST + SGST cannot be more than 100." };
    }

    return {
      mode: "split",
      igst: String(igstValue),
      cgst: String(roundTax(cgstValue)),
      sgst: String(roundTax(sgstValue)),
    };
  }

  if (!hasIgst && (hasCgst || hasSgst)) {
    return { error: "Enter both CGST and SGST together, or use only IGST." };
  }

  return { error: "Use either IGST or CGST + SGST, not both." };
}

function AddHsnCode() {
  const wrapRef = useRef(null);

  const [toasts, setToasts] = useState([]);
  const toast = (type, message, title) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setToasts((previous) => [...previous, { id, type, message, title }]);
    window.setTimeout(() => {
      setToasts((previous) => previous.filter((toastItem) => toastItem.id !== id));
    }, 2800);
  };
  const removeToast = (id) => setToasts((previous) => previous.filter((toastItem) => toastItem.id !== id));

  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");

  const [hsnCode, setHsnCode] = useState("");
  const [igst, setIgst] = useState("");
  const [cgst, setCgst] = useState("");
  const [sgst, setSgst] = useState("");

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [hasExistingHsn, setHasExistingHsn] = useState(false);
  const [existingHsnId, setExistingHsnId] = useState("");

  const [loadingCats, setLoadingCats] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [saving, setSaving] = useState(false);

  useLayoutEffect(() => {
    if (!wrapRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".hsn-anim",
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.45, stagger: 0.06, ease: "power2.out" }
      );
    }, wrapRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCats(true);
      try {
        const response = await fetch(`${ApiURl}/getMainCategory.php`);
        const data = await response.json();
        setCategories(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
        toast("error", "Failed to fetch categories.");
      } finally {
        setLoadingCats(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchExisting = async () => {
      if (!categoryId) {
        setHasExistingHsn(false);
        setExistingHsnId("");
        resetForm();
        setMessage("");
        setMessageType("info");
        return;
      }

      setCheckingExisting(true);
      setMessage("");
      setMessageType("info");

      try {
        const response = await fetch(`${ApiURl}/get_hsn_codes.php?category_id=${categoryId}`);
        const data = await response.json();

        const exists = Boolean(data?.success && Array.isArray(data?.data) && data.data.length > 0);
        setHasExistingHsn(exists);

        if (exists) {
          const existing = data.data[0];
          setExistingHsnId(existing.hsn_id || "");
          setHsnCode(existing.hsn_code || "");
          setIgst(existing.igst ?? "");
          setCgst("");
          setSgst("");
          setMessage("Existing HSN loaded. Edit and save to update it.");
          setMessageType("warning");
        } else {
          setExistingHsnId("");
          resetForm();
          setMessage("No HSN found for this category. Add a new one.");
          setMessageType("info");
        }
      } catch (error) {
        console.error("Error fetching existing HSN codes:", error);
        setHasExistingHsn(false);
        setExistingHsnId("");
      } finally {
        setCheckingExisting(false);
      }
    };

    fetchExisting();
  }, [categoryId]);

  const selectedCategoryName = useMemo(() => {
    const category = categories.find((item) => String(item.id) === String(categoryId));
    return category?.category_name || "";
  }, [categories, categoryId]);

  const isIgstMode = hasValue(igst);
  const isSplitTaxMode = hasValue(cgst) || hasValue(sgst);

  const taxPreview = useMemo(() => deriveTaxPayload({ igst, cgst, sgst }), [igst, cgst, sgst]);

  const taxHelperText = useMemo(() => {
    if (!isIgstMode && !hasValue(cgst) && !hasValue(sgst)) {
      return "Enter either IGST or both CGST and SGST. The missing values will be filled automatically.";
    }

    if (taxPreview.error) {
      return taxPreview.error;
    }

    if (taxPreview.mode === "igst") {
      return `IGST ${taxPreview.igst}% will also be saved as CGST ${taxPreview.cgst}% and SGST ${taxPreview.sgst}%.`;
    }

    return `CGST ${taxPreview.cgst}% + SGST ${taxPreview.sgst}% will also be saved as IGST ${taxPreview.igst}%.`;
  }, [cgst, isIgstMode, sgst, taxPreview]);

  const resetForm = () => {
    setHsnCode("");
    setIgst("");
    setCgst("");
    setSgst("");
  };

  const handleIgstChange = (value) => {
    const nextValue = clampNumber(value, 0, 100);
    setIgst(nextValue);

    if (nextValue !== "") {
      setCgst("");
      setSgst("");
    }
  };

  const handleCgstChange = (value) => {
    const nextValue = clampNumber(value, 0, 100);
    setCgst(nextValue);

    if (nextValue !== "") {
      setIgst("");
    }
  };

  const handleSgstChange = (value) => {
    const nextValue = clampNumber(value, 0, 100);
    setSgst(nextValue);

    if (nextValue !== "") {
      setIgst("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setMessageType("info");

    if (!categoryId || !hsnCode.trim()) {
      toast("error", "Category and HSN Code are required.", "Validation");
      return;
    }

    if (taxPreview.error) {
      toast("error", taxPreview.error, "Validation");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${ApiURl}/addHsnCode.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          main_category_id: categoryId,
          hsn_id: existingHsnId,
          hsn_code: hsnCode.trim(),
          igst: taxPreview.igst,
          cgst: taxPreview.cgst,
          sgst: taxPreview.sgst,
        }),
      });

      const data = await response.json();

      if (data?.status === "success") {
        toast("success", hasExistingHsn ? "HSN Code and taxes updated successfully." : "HSN Code and taxes added successfully.");
        setHasExistingHsn(true);
        setExistingHsnId(data?.hsn_id || existingHsnId);
        setMessage(hasExistingHsn ? "HSN Code updated successfully." : "HSN Code saved. This category now has an HSN entry.");
        setMessageType("success");
      } else {
        toast("error", data?.message || "Error saving HSN Code and taxes.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast("error", "An error occurred while saving the HSN Code and taxes.");
    } finally {
      setSaving(false);
    }
  };

  const messageClassName =
    messageType === "success"
      ? "text-green-700"
      : messageType === "warning"
      ? "text-amber-700"
      : "text-gray-700";

  return (
    <div ref={wrapRef} className="min-h-[calc(100vh-0px)] bg-gray-50">
      <ToastStack toasts={toasts} onRemove={removeToast} />

      <div className="hsn-anim mx-auto w-full max-w-4xl px-3 py-6 sm:px-6">
        <div className="hsn-anim rounded-2xl border bg-white p-4 sm:p-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">Add / Update HSN Code & Taxes</h1>
              <p className="mt-1 text-sm text-gray-600">
                Attach or update an HSN code with either IGST or CGST + SGST. Missing tax values are filled automatically.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setCategoryId("");
                resetForm();
                setMessage("");
                setMessageType("info");
                setHasExistingHsn(false);
                setExistingHsnId("");
                toast("warning", "Form cleared.");
              }}
              className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="hsn-anim mt-4 rounded-2xl border bg-white p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="category" className="block text-sm font-semibold text-gray-700">
                  Category
                </label>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <select
                    id="category"
                    className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-[#b29063] focus:ring-2 focus:ring-[#b29063]/30"
                    onChange={(e) => setCategoryId(e.target.value)}
                    value={categoryId}
                  >
                    <option value="">{loadingCats ? "Loading categories..." : "Select category"}</option>
                    {categories.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.category_name}
                      </option>
                    ))}
                  </select>

                  <div className="min-w-[170px] text-xs font-semibold text-gray-500">
                    {checkingExisting
                      ? "Checking HSN..."
                      : categoryId
                      ? hasExistingHsn
                        ? "HSN exists"
                        : "No HSN found"
                      : "--"}
                  </div>
                </div>

                {categoryId && selectedCategoryName && (
                  <p className="mt-2 text-xs text-gray-500">
                    Selected: <span className="font-semibold">{selectedCategoryName}</span>
                  </p>
                )}

                {hasExistingHsn && (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    This category already has an HSN code. Edit the fields below and save to update it.
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="hsnCode" className="block text-sm font-semibold text-gray-700">
                  HSN Code
                </label>
                <input
                  value={hsnCode}
                  onChange={(e) => setHsnCode(e.target.value)}
                  type="text"
                  id="hsnCode"
                  className="mt-2 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-[#b29063] focus:ring-2 focus:ring-[#b29063]/30"
                  placeholder="Enter HSN Code"
                  disabled={!categoryId}
                />
                <p className="mt-1 text-xs text-gray-500">Example: 6109, 4202, etc.</p>
              </div>

              <div>
                <label htmlFor="igst" className="block text-sm font-semibold text-gray-700">
                  IGST (%)
                </label>
                <input
                  value={igst}
                  onChange={(e) => handleIgstChange(e.target.value)}
                  type="number"
                  id="igst"
                  className="mt-2 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-[#b29063] focus:ring-2 focus:ring-[#b29063]/30"
                  placeholder={isSplitTaxMode ? "Clear CGST/SGST to use IGST" : "0 - 100"}
                  min="0"
                  max="100"
                  step="0.01"
                  disabled={!categoryId || isSplitTaxMode}
                />
                <p className="mt-1 text-xs text-gray-500">Use this alone for interstate tax.</p>
              </div>

              <div>
                <label htmlFor="cgst" className="block text-sm font-semibold text-gray-700">
                  CGST (%)
                </label>
                <input
                  value={cgst}
                  onChange={(e) => handleCgstChange(e.target.value)}
                  type="number"
                  id="cgst"
                  className="mt-2 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-[#b29063] focus:ring-2 focus:ring-[#b29063]/30"
                  placeholder={isIgstMode ? "Auto from IGST" : "0 - 100"}
                  min="0"
                  max="100"
                  step="0.01"
                  disabled={!categoryId || isIgstMode}
                />
                <p className="mt-1 text-xs text-gray-500">Enter with SGST for intrastate tax.</p>
              </div>

              <div>
                <label htmlFor="sgst" className="block text-sm font-semibold text-gray-700">
                  SGST (%)
                </label>
                <input
                  value={sgst}
                  onChange={(e) => handleSgstChange(e.target.value)}
                  type="number"
                  id="sgst"
                  className="mt-2 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:border-[#b29063] focus:ring-2 focus:ring-[#b29063]/30"
                  placeholder={isIgstMode ? "Auto from IGST" : "0 - 100"}
                  min="0"
                  max="100"
                  step="0.01"
                  disabled={!categoryId || isIgstMode}
                />
                <p className="mt-1 text-xs text-gray-500">Enter with CGST for intrastate tax.</p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs text-gray-500">{taxHelperText}</p>
              {message && <p className={`text-sm ${messageClassName}`}>{message}</p>}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-gray-500">
                Tax fields accept values between 0 and 100. Enter either IGST or both CGST and SGST.
              </div>

              <button
                type="submit"
                disabled={saving || !categoryId}
                className="rounded-xl bg-[#b29063] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#a08053] disabled:opacity-50"
              >
                {saving ? "Saving..." : hasExistingHsn ? "Update HSN Code & Taxes" : "Add HSN Code & Taxes"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddHsnCode;
